/**
 * Abandoned Cart Popup Tracker - Recurring Version
 * Shows popup 1 minute after user adds to cart
 * Repeats every 1 minute if user ignores it
 * Stops when cart is empty
 */

class CartAbandonTracker {
  constructor() {
    this.DELAY_TIME = 2 * 60 * 1000; // 1 minute in milliseconds
    this.RETURN_DELAY = 5 * 60 * 1000; // 5 minutes delay when user returns
    this.SESSION_KEY = 'cart_session_active';
    this.STORAGE_KEY = 'cart_abandon_timer';
    this.POPUP_COUNT_KEY = 'cart_abandon_popup_count';
    this.timer = null;
    this.init();
  }

  init() {
    // Listen for add to cart events
    this.attachCartListeners();
    
    // Check if there's an existing timer on page load
    this.checkExistingTimer();
    
    // Reset timer when user goes to checkout
    this.attachCheckoutListeners();
  }

  attachCartListeners() {
    // Listen for native add to cart
    document.addEventListener('submit', (e) => {
      if (e.target.matches('form[action*="/cart/add"]')) {
        this.startTimer();
      }
    });

    // Listen for AJAX add to cart (common in Shopify themes)
    const originalFetch = window.fetch;
    window.fetch = (...args) => {
      const url = args[0];
      if (typeof url === 'string' && url.includes('/cart/add')) {
        this.startTimer();
      }
      return originalFetch(...args);
    };

    // Listen for cart updates via custom events
    document.addEventListener('cart:updated', () => {
      this.startTimer();
    });
  }

  attachCheckoutListeners() {
    // Reset if user visits checkout (but NOT cart page)
    if (window.location.pathname.includes('/checkout')) {
      this.cancelTimer();
    }

    // Listen for clicks on checkout links
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (link && link.href.includes('/checkout')) {
        this.cancelTimer();
      }
    });
  }

  startTimer() {
    // Clear any existing timer
    this.cancelTimer();

    // Set timer data
    const triggerTime = Date.now() + this.DELAY_TIME;
    localStorage.setItem(this.STORAGE_KEY, triggerTime);

    // Start countdown
    this.timer = setTimeout(() => {
      this.showPopup();
    }, this.DELAY_TIME);

    console.log('Cart abandon timer started - will trigger in 1 minute');
  }

  checkExistingTimer() {
    const triggerTime = localStorage.getItem(this.STORAGE_KEY);
    
    if (!triggerTime) return;

    const timeRemaining = parseInt(triggerTime) - Date.now();

    if (timeRemaining > 0) {
      // Timer still active, resume it
      this.timer = setTimeout(() => {
        this.showPopup();
      }, timeRemaining);
      
      console.log(`Cart abandon timer resumed - ${Math.round(timeRemaining/1000)}s remaining`);
    } else if (timeRemaining <= 0) {
      // Timer expired, show popup immediately
      this.showPopup();
    }
  }

  cancelTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.POPUP_COUNT_KEY);
  }

  async showPopup() {
    localStorage.removeItem(this.STORAGE_KEY);

    // Get current cart data
    const cartData = await this.fetchCart();
    
    if (!cartData || cartData.item_count === 0) {
      console.log('Cart is empty, stopping popup cycle');
      this.cancelTimer();
      return;
    }

    // Increment popup count
    const popupCount = parseInt(localStorage.getItem(this.POPUP_COUNT_KEY) || '0') + 1;
    localStorage.setItem(this.POPUP_COUNT_KEY, popupCount.toString());

    // Update popup content
    this.updatePopupContent(cartData, popupCount);
    
    // Show the popup
    const popup = document.getElementById('cart-abandon-popup');
    if (popup) {
      popup.style.display = 'block';
      document.body.style.overflow = 'hidden';
      
      // Track event (optional - for analytics)
      this.trackEvent('abandon_cart_popup_shown', { popup_number: popupCount });
      
      console.log(`Popup shown (${popupCount} time${popupCount > 1 ? 's' : ''})`);
    }

    // Schedule next popup in 1 minute (recurring)
    this.startTimer();
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  updatePopupContent(cartData, popupCount) {
    // Update item count
    const countEl = document.getElementById('cart-item-count');
    if (countEl) {
      countEl.textContent = cartData.item_count;
    }

    // Optional: Update popup count display
    const popupCountEl = document.getElementById('popup-count');
    if (popupCountEl) {
      popupCountEl.textContent = popupCount;
    }

    // Update products list
    const productsContainer = document.getElementById('cart-abandon-products');
    if (productsContainer && cartData.items.length > 0) {
      productsContainer.innerHTML = cartData.items.map(item => `
        <div class="cart-abandon-product-item">
          <img src="${item.featured_image.url}" 
               alt="${item.product_title}" 
               class="cart-abandon-product-image">
          <div class="cart-abandon-product-info">
            <div class="cart-abandon-product-title">${item.product_title}</div>
            <div class="cart-abandon-product-price">${this.formatMoney(item.final_line_price)}</div>
          </div>
        </div>
      `).join('');
    }
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: window.themeVariables?.settings?.cartCurrency || 'USD'
    });
  }

  trackEvent(eventName, params = {}) {
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, params);
    }
    
    // Meta Pixel
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', eventName, params);
    }
  }
}

// Close popup function (global for inline onclick)
window.closeAbandonPopup = function() {
  const popup = document.getElementById('cart-abandon-popup');
  if (popup) {
    popup.style.display = 'none';
    document.body.style.overflow = '';
  }
};

// Close on overlay click
document.addEventListener('click', (e) => {
  if (e.target.classList.contains('cart-abandon-overlay') || 
      e.target.closest('.cart-abandon-close')) {
    window.closeAbandonPopup();
  }
});

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartAbandonTracker();
  });
} else {
  new CartAbandonTracker();
}